/**
 * Asset registry.
 *
 * Components resolve art through `A(id)` and never hardcode a path. `art.generated.ts` is
 * rebuilt by tools/manifest.mjs after every generation batch and contains only ids whose
 * file actually exists, so a half-generated library degrades to a sensible stand-in rather
 * than a broken image.
 */

import { ALT, ASPECT, GENERATED } from './art.generated'

/**
 * Every id this module resolves, as a union of 1064 string literals, emitted by tools/manifest.mjs
 * in the same run that writes `art.generated.ts`.
 *
 * `AssetId` is deliberately NOT narrowed to it, and neither is `A()`. The two types answer different
 * questions. `KnownAssetId` is for an id a developer WRITES: a row in a mock table, a JSX prop, a
 * fallback. Those are known at compile time and a typo in one is a bug, so the union makes it
 * unwritable rather than merely findable. `AssetId` is for an id that ARRIVES at runtime:
 * `player.portraitId`, `who.roleArtId`, `offer.artId`, `family.crestId`. Those come off the wire out
 * of content JSON that is hot-swappable in KV without a deploy, and the server validates them with a
 * regex rather than an enum (server/src/content/schema.ts), so the client cannot have compiled
 * against their values. Narrowing `A()` would reject them for being honest.
 *
 * Every `KnownAssetId` is assignable to `AssetId`, so annotating a table costs its call sites
 * nothing.
 */
import type { KnownAssetId } from './art.ids.generated'
export type { KnownAssetId }
import { API_ORIGIN } from './build'

export type AssetId = string

/**
 * PHASE 26. `/art` is same-origin in every build but Jest's, where the page is served from their
 * CDN and every picture is fetched from ashport.city, which serves `/art/*` from R2 through the
 * Worker. `API_ORIGIN` is empty everywhere else, so this is `/art/` as it always was.
 */
const BASE = API_ORIGIN + '/art/'

/**
 * Hand-kept aliases for the master reference pack. Short ids the screens read well with.
 *
 * Exported as data, with REWRITE and heroAlias below, because tools/manifest.mjs enumerates the id
 * space from these three tables to emit `KnownAssetId` and tools/content/validate.mjs recomputes it
 * to prove the emitted file is fresh. Reading the tables beats copying them: a new alias widens the
 * union and the gate's net in the same edit that widens the resolver.
 */
export const ALIAS: Record<string, string> = {
  'brand.skyline': 'brand/skyline-night.png',
  'city.master': 'city/master-neutral.png',
  'ui.materials': 'ui/material-board.png',
  'items.materials': 'items/material-board.png',
  'char.sallie': 'characters/sallie-marchetti.png',
  'char.vincenzo': 'characters/vincenzo-aiello.png',
  'district.dockside': 'districts/dockside-hero.png',
  'district.ironbound': 'districts/ironbound-hero.png',
  'district.little_sicily': 'districts/little-sicily-hero.png',
  'district.verity_hill': 'districts/verity-hill-hero.png',
  'district.slaughterhouse_flats': 'districts/slaughterhouse-flats-hero.png',
  'district.kepler_row': 'districts/kepler-row-hero.png',
}

/**
 * Per-category stand-ins, used while a batch is still generating. Chosen so the layout
 * still reads correctly: a missing portrait shows a portrait, not a landscape.
 */
const STANDIN: [RegExp, KnownAssetId][] = [
  [/portrait|character|sponsor|wanted|face|target/, 'char.sallie'],
  [/district|city|territory|map|block/, 'city.master'],
  [/weapon|armor|vehicle|item|contraband|material|cosmetic|stockpile|market|crate|badge/, 'items.materials'],
  [/^ui\.|panel|frame|plate|button|tab|texture/, 'ui.materials'],
  [/job|crime|contract|scene|war|scanner|hero|brand|era|alert|jail/, 'district.ironbound'],
]

/** Stable small hash, so a given missing id always resolves to the same substitute. */
function hash(s: string): number {
  let h = 2166136261
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return (h >>> 0)
}

const siblingCache = new Map<string, string[]>()

/**
 * Walk up the dotted id and borrow a real sibling.
 *
 * A screen written against `crimes.featured.casino_heist` when the library actually shipped
 * `crimes.featured.casino_cage` still gets a *casino-adjacent* image rather than a generic
 * plate. The sibling is chosen by hashing the requested id, so six unresolved item renders
 * on one row pick six different substitutes instead of repeating one placeholder.
 */
function nearest(rawId: AssetId): string | undefined {
  const id = rewrite(rawId) ?? rawId
  const parts = id.split('.')
  for (let n = parts.length - 1; n >= 1; n--) {
    const prefix = parts.slice(0, n).join('.') + '.'
    let sibs = siblingCache.get(prefix)
    if (!sibs) {
      sibs = Object.keys(GENERATED).filter((k) => k.startsWith(prefix)).sort()
      siblingCache.set(prefix, sibs)
    }
    if (sibs.length) return GENERATED[sibs[hash(rawId) % sibs.length]]
  }
  return undefined
}

/**
 * Namespace drift between what a screen was written against and what the generator
 * shipped. Rewriting the prefix is cheaper and safer than editing ten screens, and it keeps
 * the screens readable in their own vocabulary.
 */
export const REWRITE: [string, string][] = [
  ['cosmetic.', 'cosmetics.'],
  ['era.', 'ledger.eras.'],
  ['legacy.', 'icons.legacy.'],
  ['badge.', 'icons.legacy.'],
  ['territory.raid_modal', 'territory.modals.raid_prep'],
  ['territory.scout_modal', 'territory.modals.scout_surveillance'],
  ['territory.defend_modal', 'territory.modals.defend'],
]

/**
 * The first dotted segment of every id shape this module accepts that `art/manifest.json` does
 * not list: the ALIAS keys and the REWRITE sources.
 *
 * It exists for tools/content/validate.mjs, which decides whether a string literal in a screen
 * LOOKS like an asset id by testing its head against the manifest's namespaces. Those namespaces
 * alone are not the whole vocabulary. `char` is the load-bearing example: it is an ALIAS head and
 * not a generated namespace, so a head test built from the manifest would silently skip every
 * `char.sallie` and `char.vincenzo` written in the tree. `cosmetic`, `era`, `legacy` and `badge`
 * are the same story on the REWRITE side.
 *
 * Derived from the two tables rather than typed out beside them, so a new alias or rewrite widens
 * the gate's net in the same edit that widens the resolver.
 */
export const ID_HEADS: string[] = [
  ...new Set(
    [...Object.keys(ALIAS), ...REWRITE.map(([from]) => from)].map((k) => k.split('.')[0]),
  ),
]

/**
 * District heroes shipped under two conventions: the six anchors are
 * `district.<name>.hero`, the six later ones are `districts.<name>_hero`. Rather than rename
 * files (which would invalidate every sidecar and manifest entry), both spellings resolve.
 */
export function heroAlias(id: AssetId): AssetId | undefined {
  let m = /^district\.(.+)\.hero$/.exec(id)
  if (m) return `districts.${m[1]}_hero`
  m = /^districts\.(.+)_hero$/.exec(id)
  if (m) return `district.${m[1]}.hero`
  return undefined
}

function rewrite(id: AssetId): AssetId | undefined {
  const hero = heroAlias(id)
  if (hero && GENERATED[hero]) return hero
  for (const [from, to] of REWRITE) {
    if (id.startsWith(from)) return to + id.slice(from.length)
  }
  return undefined
}

function resolve(id: AssetId): string | undefined {
  const direct = GENERATED[id] ?? ALIAS[id]
  if (direct) return direct
  const alt = rewrite(id)
  return alt ? GENERATED[alt] : undefined
}

/**
 * Ids already complained about, so a substitution inside a render loop is one line and not
 * sixty a second. Nothing here is load bearing: it is a Set to keep the console readable.
 */
const complained = new Set<string>()

/**
 * SAY SOMETHING when a substitution happens, in development.
 *
 * This is the root cause of the whole class. `A()` cannot throw, because it is called from
 * render paths with ids the server supplied and a missing picture must never take a screen down.
 * But it was also completely silent, so the failure mode was a plausible WRONG image under alt
 * text describing the right one, with a green build and a clean console.
 *
 * Five instances shipped that way and every one was found by eye: a Ledger banner rendering
 * `ledger.eras.reckoning` under alt text reading "Ashport harbor under the Accord", and four
 * rows of an Era history table where two Eras wore the same wrong banner. The static gate now
 * refuses the ones that can be found by reading the source, and this catches the rest at the
 * moment they happen, which is the only place a computed id can be caught at all.
 *
 * Stripped from a production build: `import.meta.env.DEV` is a compile-time constant, so the
 * whole branch is dead code the minifier removes.
 */
function grumble(id: AssetId, substituted: string, how: string): void {
  if (!import.meta.env.DEV) return
  if (complained.has(id)) return
  complained.add(id)
  console.warn(
    `[art] "${id}" does not resolve. Rendering "${substituted}" instead, chosen by ${how}. ` +
      `A() never throws, so this is the only warning you get.`,
  )
}

/**
 * An id BUILT at runtime, widened on purpose and marked where it happens.
 *
 * A template literal with a hole infers as a template literal TYPE rather than as `string`, so
 * it is neither a checkable literal nor an honest variable and `A()` refuses it. Widening it
 * here rather than loosening the signature keeps the guarantee intact and makes every
 * unverifiable id greppable in one word, which is the only handle anybody has on the calls the
 * content gate also cannot see. If a `dyn()` renders the wrong picture, the dev console says so
 * the first time it happens.
 */
export const dyn = (id: string): AssetId => id
/**
 * A LITERAL has to be a real id; a VARIABLE does not have to be anything.
 *
 * `A()` is called two ways and only one of them can be checked at compile time. A developer
 * writing `A('families.crests.marchetti_crow')` is making a claim the compiler can verify. A
 * screen writing `A(m.portraitId)` is passing a string the SERVER chose, which cannot be a union
 * and must never be rejected: a missing picture may not take a screen down.
 *
 * The conditional does exactly that split. When `T` infers as `string`, which is every variable,
 * `string extends T` holds and the parameter widens to `T` and anything passes. When `T` infers
 * as a literal type, which is every hand-written id, it must be in `KnownAssetId` or the
 * parameter collapses to `KnownAssetId` and the call is a type error naming the nearest real id:
 *
 *   error TS2820: Type '"era.3.harbor_accord.plate"' is not assignable to type
 *   'KnownAssetId'. Did you mean '"era.harbor_accord"'?
 *
 * The fallback gets the same treatment, because `A()` runs it through `resolve()` and then
 * `nearest()` (below), so a dangling fallback substitutes just as silently as a dangling id.
 *
 * This is the half the content gate could not close by itself. The gate reads source and can
 * only see what it can parse; this refuses the id before it is ever written down.
 */
export function A<T extends string, F extends string = string>(
  id: string extends T ? T : T extends KnownAssetId ? T : KnownAssetId,
  fallback?: string extends F ? F : F extends KnownAssetId ? F : KnownAssetId,
): string
export function A(id: AssetId, fallback?: AssetId): string {
  const hit = resolve(id)
  if (hit) return BASE + hit

  // A real sibling in the right category beats a caller-supplied generic plate, so the
  // hierarchy walk runs before the explicit fallback rather than after it.
  const near = nearest(id)
  if (near) {
    grumble(id, near, 'the sibling walk')
    return BASE + near
  }

  if (fallback) {
    const fb = resolve(fallback) ?? nearest(fallback)
    if (fb) {
      grumble(id, fb, `the caller's fallback "${fallback}"`)
      return BASE + fb
    }
  }

  for (const [re, alias] of STANDIN) {
    if (re.test(id)) {
      grumble(id, alias, 'a category stand-in')
      return BASE + ALIAS[alias]
    }
  }
  grumble(id, 'ui.materials', 'the last-resort stand-in')
  return BASE + ALIAS['ui.materials']
}

/** True when the real asset exists. Screens can use this to hide optional decoration. */
export function has(id: AssetId): boolean {
  return Boolean(resolve(id))
}

/** Aspect ratio of the master, so layouts can reserve space before the image loads. */
export function ratio(id: AssetId, dflt = 1.7778): number {
  return ASPECT[id] ?? dflt
}

/** Generated alt text derived from the art-direction prompt. Always pass a better one if you have it. */
export function altOf(id: AssetId, dflt = ''): string {
  return ALT[id] ?? dflt
}

/**
 * Pick the nth asset from a category prefix. Lets a screen render "twelve weapons"
 * without knowing which twelve exist yet.
 */
const cache = new Map<string, string[]>()
export function pick(prefix: string): string[] {
  let hit = cache.get(prefix)
  if (!hit) {
    hit = Object.keys(GENERATED).filter((k) => k.startsWith(prefix)).sort()
    cache.set(prefix, hit)
  }
  return hit
}

/**
 * `fallback` is narrowed where `A()`'s is not, and the difference is where the value comes from. A
 * fallback here is only ever reached when the whole prefix is empty, so it is a backstop somebody
 * types into a screen rather than anything a payload could carry. `prefix` stays `string`: a prefix
 * is not an id and no union describes one.
 */
export function nth(prefix: string, i: number, fallback?: KnownAssetId): string {
  const list = pick(prefix)
  return list.length ? A(list[i % list.length]) : A(fallback ?? prefix)
}
